/* eslint-disable */
// src/App.js

import { useState, useEffect, useMemo } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import CircularProgress from "@mui/material/CircularProgress";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import routes from "routes";
import PrivateRoute from "components/PrivateRoute";
import {
  useMaterialUIController,
  setMiniSidenav,
} from "context";
import SignIn from "layouts/authentication/sign-in";
import brandWhite from "assets/images/logo-g-claro-sin-fondo.png";
import brandDark from "assets/images/logo-g-oscuro-sin-fondo.png";
import { jwtDecode } from "jwt-decode";
import axios from "services/axiosConfig";
import { getCurrentRole, getCurrentUsername, hasRoleAccess } from "utils/auth";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { NotificationProvider } from "context/NotificationContext";

export default function App() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");

    let timerId;
    let isMounted = true;

    const finishChecking = () => {
      if (isMounted) setAuthChecking(false);
    };

    const redirectAuthenticatedAwayFromLogin = () => {
      if (window.location.pathname === "/authentication/sign-in") {
        navigate("/dashboard", { replace: true });
      }
    };

    const clearLocalSession = () => {
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      localStorage.removeItem("username");
      if (isMounted) setUser(null);
    };

    const persistAccessToken = (newToken) => {
      localStorage.setItem("token", newToken);
      const role = getCurrentRole();
      if (role) localStorage.setItem("role", role);
      else localStorage.removeItem("role");
      const username = getCurrentUsername();
      if (username) localStorage.setItem("username", username);
      else localStorage.removeItem("username");
      if (isMounted) setUser(true);
      redirectAuthenticatedAwayFromLogin();
    };

    const refreshAccessToken = async (currToken) => {
      const config = currToken
        ? { headers: { Authorization: `Bearer ${currToken}` } }
        : {};
      const resp = await axios.post(
        "/refresh/refresh-token",
        {},
        config
      );
      const newToken = resp.data.accessToken;
      persistAccessToken(newToken);
      return newToken;
    };

    const handleRefreshError = (err, { clearOnAuthFailure = true, hadToken = false } = {}) => {
      const status = err?.response?.status;
      const isAuthFailure = status === 401 || status === 403;

      if (isAuthFailure && clearOnAuthFailure) {
        console.warn("[auth] clearing_local_session", {
          status,
          message: err?.response?.data?.message,
        });
        clearLocalSession();
        navigate("/authentication/sign-in");
        return;
      }

      console.warn("[auth] refresh_failed_without_local_clear", {
        status,
        message: err?.response?.data?.message,
      });
      if (process.env.NODE_ENV !== "production") {
        console.error("Refresh fallido sin limpiar sesion local:", err);
      }
      if (isMounted) setUser(Boolean(hadToken));
    };

    const scheduleRefresh = (delay, currToken) => {
      timerId = setTimeout(async () => {
        try {
          const newToken = await refreshAccessToken(currToken);
          const { exp } = jwtDecode(newToken);
          const nextDelay = Math.max((exp * 1000 - Date.now()) - 3000, 2000);
          scheduleRefresh(nextDelay, newToken);
        } catch (err) {
          handleRefreshError(err, { hadToken: true });
        }
      }, delay);
    };

    const initializeSession = async () => {
      if (!token || token.split(".").length !== 3) {
        try {
          const newToken = await refreshAccessToken(null);
          const { exp: newExp } = jwtDecode(newToken);
          const nextDelay = Math.max((newExp * 1000 - Date.now()) - 3000, 2000);
          scheduleRefresh(nextDelay, newToken);
        } catch (err) {
          handleRefreshError(err, { hadToken: false });
        } finally {
          finishChecking();
        }
        return;
      }

      const { exp } = jwtDecode(token);
      const refreshInMs = (exp * 1000 - Date.now()) - 3000;

      if (refreshInMs <= 0) {
        try {
          const newToken = await refreshAccessToken(token);
          const { exp: newExp } = jwtDecode(newToken);
          const nextDelay = Math.max((newExp * 1000 - Date.now()) - 3000, 2000);
          scheduleRefresh(nextDelay, newToken);
        } catch (err) {
          handleRefreshError(err, { hadToken: true });
        } finally {
          finishChecking();
        }
        return;
      }

      persistAccessToken(token);
      scheduleRefresh(Math.max(refreshInMs, 2000), token);
      finishChecking();
    };

    initializeSession().catch((err) => {
      if (process.env.NODE_ENV !== "production") console.error("Token inválido:", err);
      clearLocalSession();
      navigate("/authentication/sign-in");
      finishChecking();
    });

    return () => {
      isMounted = false;
      clearTimeout(timerId);
    };
  }, [navigate]);

  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) return getRoutes(route.collapse);
      if (route.route) {
        if (route.private) {
          if (!hasRoleAccess(route.roles)) return null;
          return (
            <Route key={route.key} element={<PrivateRoute allowedRoles={route.roles} />}>
              <Route path={route.route} element={route.component} />
            </Route>
          );
        }
        return <Route key={route.key} path={route.route} element={route.component} />;
      }
      return null;
    });

  const chosenTheme =
    direction === "rtl"
      ? darkMode
        ? themeDarkRTL
        : themeRTL
      : darkMode
      ? themeDark
      : theme;

  const routeListForSidenav = routes.filter((r) => !r.private || hasRoleAccess(r.roles));

  return (
    <ThemeProvider theme={chosenTheme}>
      <NotificationProvider>
      <CssBaseline />
      {!authChecking && layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={
              (transparentSidenav && !darkMode) || whiteSidenav
                ? brandDark
                : brandWhite
            }
            brandName="Entretenimientos Gardel"
            routes={routeListForSidenav}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
        </>
      )}
      {!authChecking && layout === "vr" && <Configurator />}

      {authChecking ? (
        <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh" gap={2}>
          <CircularProgress color="info" />
          <MDTypography variant="body2" color="text">
            Verificando sesión...
          </MDTypography>
        </MDBox>
      ) : !user ? (
        <SignIn onLogin={() => setUser(true)} />
      ) : (
        <Routes>
          {getRoutes(routeListForSidenav)}
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      )}
      </NotificationProvider>
    </ThemeProvider>
  );
}
